import { Component, Input } from '@angular/core';
import { IOrder } from '../../model/models/order/order-interface';
import { SocialSharing } from '@ionic-native/social-sharing';
import { TranslateService } from '@ngx-translate/core';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { ResourceProvider } from '../../providers/resource/resource';
import { LoadingProvider } from '../../providers/loading/loading';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { SegmentProvider } from '../../providers/analytics/segment';
import { SocialProvider } from '../../providers/social/social';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { Platform } from 'ionic-angular';
import { RouteProvider } from '../../providers/route/route';
import { APP_CONFIG } from '../../environment';
import { brandings } from '../../environment/branding/brandings';
import { CustomerProvider } from '../../providers/customer/customer';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { OrderMapper } from '../../providers/order/order.mapper';
import { SendFlowProvider } from '../../providers/send-flow/send-flow';
import { getCurrencyDisplayName } from '../../pipes/currency-pipe';

@Component({
  selector: 'rmt-share-order',
  templateUrl: 'rmt-share-order.html'
})
export class RmtShareOrderComponent {
  @Input() order: IOrder;
  @Input() orderPromise;
  @Input() analyticsLocation: String;
  @Input() footer: boolean = false;
  text: string;
  message: string = '';
  messageDetails: any;

  constructor(private loadingProvider: LoadingProvider,
              private resourceProvider: ResourceProvider,
              private platform: Platform,
              private socialProvider: SocialProvider,
              private routeProvider: RouteProvider,
              public translate: TranslateService,
              private analytics: SegmentProvider,
              private customerProvider: CustomerProvider,
              private sendFlowProvider: SendFlowProvider) {
  }

  async shareTransactionDetails() {
    this.loadingProvider.showDelayed();
    if (this.orderPromise)
      this.order = await this.orderPromise;

    this.initMessageDetails(this.order);

    const pendingWalletType = this.sendFlowProvider.getPendingWalletType();
    const transactionId = (this.order && this.order.orderNumber) ? this.order.orderNumber : null;
    this.analytics.trackEvent({ eventName: AnalyticsEventType.TransferDetailsShared, analyticsData: {
      location: this.analyticsLocation,
      transactionId: transactionId,
      transactionStatus: (this.order && this.order.orderStatus) ? this.order.orderStatus : null,
      pendingTransactionId: pendingWalletType ? transactionId : null,
      pendingWalletType: pendingWalletType ? pendingWalletType : null,
    }});

    await this.createShareMessage();

    if (this.message)
      this.message = this.message.replace(/<br\/>/g, '\n');

    this.loadingProvider.hide();
    LoggerProvider.info(this, 'Shared order details', this.message);

    this.checkPlatform();
  }

  initMessageDetails(order) {
     let locale = this.customerProvider.getCurrentLanguage();
    this.messageDetails = {
      amount: order.amountTo,
      currency: getCurrencyDisplayName(order.currencyTo),
      currencyTo: getCurrencyDisplayName(order.currencyTo),
      orderNumber: order.orderNumber,
      orderPin: order.pin,
      orderPIN: order.pin,
      PINnumber: order.pin,
      PINNumber: order.pin,
      recipientFirstName: order.recipient.firstName,
      transactionId: order.orderNumber,
      // Public site is case-sensitive on the locale path segment; share links embed it (ria.mt/{{locale}}/...) (OM-19681)
      locale: (locale) ? locale.toLowerCase() : '',
      bankAccount: order.recipient.bankAccountNumber || order.recipient.bankAccountNumberUnitary || ''
    };
  }

  async createShareMessage() {
    if (this.order) {
      switch (this.order.deliveryMethodInternal) {
        case DeliveryMethodType.BankDeposit:
        case DeliveryMethodType.BankDepositNextDay:
          this.displayBankDepositMessage();
          break;
        case DeliveryMethodType.DebitCard:
          this.displayDebitCardDepositMessage();
          break;
        case DeliveryMethodType.OfficePickup:
          await this.displayOfficePickupMessage();
          break;
        case DeliveryMethodType.HomeDelivery:
          this.displayHomeDeliveryMessage();
          break;
        case DeliveryMethodType.Alipay:
        case DeliveryMethodType.PIX:
        case DeliveryMethodType.UPI:
        case DeliveryMethodType.BreB:
          this.displayVPAMessage();
          break;
        case DeliveryMethodType.MobilePayment:
          this.displayWalletMessage();
          break;
        default:
          this.displayDefaultMessage();
          break;
      }
    } else {
      LoggerProvider.error(this, LoggerEvent.GeneralException, "Cannot find order in context");
      RmtPopupComponent.showExceptionWithLogTranslation(this, "error", 'global.myRia.jsonContent.OopsInfo');
    }
  }

  displayBankDepositMessage() {
    this.setMessageDetailsBankAccount();
    const baseKey = 'page.sendCompletedPage.socialShareMessageBank';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  displayDebitCardDepositMessage() {
    this.setMessageDetailsBankAccount();
    const baseKey = 'page.sendCompletePage.socialShareMessageDebitCard';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  async displayOfficePickupMessage() {
    if(APP_CONFIG.branding === brandings.Walmart) this.displayWalmartMessage();
    else {
      try {
        const country = await this.resourceProvider.getCountryByValue(this.order.countryTo);
        if (country && country.countryName) this.messageDetails.countryTo = country.countryName;

        this.displayLocationMessage(country);
      } catch(ex) { LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to get country for share order component'); }
    }
  }

  setMessageDetailsLocation() {
    if (this.order.agentToLocation)
        this.messageDetails.location = this.order.agentToLocation.agentName + (this.order.agentToLocation.locationAddress ? ', ' + this.order.agentToLocation.locationAddress : '');
    else
      this.messageDetails.location = this.order.agentTo.name;
  }

  setMessageDetailsBankAccount() {
    if (this.order.recipient.bankAccountNumber)
      this.messageDetails.bankAccount = this.order.recipient.bankAccountNumber;
    else
      this.messageDetails.bankAccount = this.order.recipient.bankAccountNumberUnitary;
  }

  setMessageDetailsWallet() {
    this.messageDetails.orderNumber = this.order.orderNumber;
    this.messageDetails.transactionId = this.order.orderNumber;
    this.messageDetails.wallet = this.order.agentToLocation.locationName;
  }

  displayHomeDeliveryMessage() {
    if (this.order.agentTo && this.order.agentTo.correspondentPayoutBrand && this.order.agentTo.correspondentPayoutBrand !== '') {
      this.messageDetails.location = this.order.agentTo.correspondentPayoutBrand;
    } else {
      this.messageDetails.location = this.order.agentTo ? this.order.agentTo.name : "";
    }
    this.messageDetails.correspondent = this.messageDetails.location;
    const baseKey = 'page.sendCompletedPage.socialShareMessageHomeDelivery';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  displayVPAMessage() {
    this.setMessageDetailsBankAccount();
    const baseKey = 'page.sendCompletedPage.socialShareMessageVPA';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  displayWalletMessage() {
    this.setMessageDetailsWallet();
    const baseKey = 'page.sendCompletedPage.socialShareMessageWallet';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  displayDefaultMessage() {
    this.message = `${this.translate.instant('page.sendCompletedPage.orderNumberLabel')} ${this.order.orderNumber}`;
  }

  displayWalmartMessage() {
    this.messageDetails.selectedCorrespondent = this.order.agentTo.name;
    const baseKey = 'page.sendCompletedPage.socialShareMessagePickUp';
    const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
    this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
  }

  displayLocationMessage(country) {
    if (country && country.isOpenPaymentCountry) {
      const baseKey = 'page.sendCompletedPage.socialShareMessageOpenPayment';
      const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
      this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
    }
    else if (this.order.agentTo && this.order.agentTo.isOpenPayment) {
      if (this.order.agentTo.correspondentPayoutBrand && this.order.agentTo.correspondentPayoutBrand !== '') {
        this.messageDetails.correspondent = this.order.agentTo.correspondentPayoutBrand;
      } else {
        this.messageDetails.correspondent = this.order.agentTo.name;
      }
      this.messageDetails.destinationCountry = this.messageDetails.countryTo;
      const baseKey = 'page.sendCompletedPage.socialShareMessageOpenNetwork';
      const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
      this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
    }
    else {
      this.setMessageDetailsLocation();
      const baseKey = 'page.sendCompletedPage.socialShareMessagePointToPoint';
      const translationKey = this.getTranslationKeyWithFallback(baseKey, this.order.countryTo, this.order.agentTo);
      this.message = this.getTranslatedMessageWithFallback(this.translate, translationKey, baseKey, this.messageDetails);
    }
  }

  checkPlatform() {
    // WORKDAROUND: there is a very weird bug on Android where when you share and go back it can under certain circumstances pop you pack to PreLoginPage even while logged in !
    // (i.e. in SendCompletedPage it happens but only when you clicked on ViewReceipt first, so seems to be something with the navigation)
    // UPDATE: I think that can be removed the root of that issue was very likely: "platform.resume.subscribe(() => {this.initAppNav()});" in app.component.ts
    // Won't remove it now as it's too risky but should be removed for the next release
    if (this.platform.is('android')) {
      this.routeProvider.openModal(this.routeProvider.pages.HrefPage, { socialMessage: this.message }, null, 'share-order-dummy-page');
    }
    else if (this.platform.is('cordova')) {
      this.socialProvider.share(this.message);
    }
  }

  getTranslationKeyWithFallback(baseKey: string, countryTo?: string, agentTo?: any): string {
    // Translation keys to try, starting with the most specific and falling back to the least specific
    let agentToId = agentTo ? agentTo.id : null;

    if(!agentToId)
      agentToId = this.order.agentToId || null;

    const keysToTry: string[] = [];
    if (countryTo && agentToId) {
      keysToTry.push(`${baseKey}_${countryTo}_${agentToId}`);
    }
    if (countryTo) {
      keysToTry.push(`${baseKey}_${countryTo}`);
    }
    keysToTry.push(baseKey); // The base key is always the final fallback
    // check if the translation is available
    for (const keyToTry of keysToTry) {
      const translatedValue = this.translate.instant(keyToTry);
      if (!this.isTranslationMissing(translatedValue, keyToTry)) {
        return keyToTry;
      }
    }
    return baseKey;
  }

  isTranslationMissing(translatedValue: string, translationKey: string): boolean {
    return (
      translatedValue.trim().endsWith('NOT FOUND') || // Case when 'NOT FOUND' is appended
      translatedValue === translationKey // Case when the value returned is just the key itself in default ngx-translate cases
    );
  }

  getTranslatedMessageWithFallback(translate: TranslateService, primaryKey: string, baseKey: string, messageDetails: any): string {
    let translatedValue = translate.instant(primaryKey, messageDetails);

    if (this.isTranslationMissing(translatedValue, primaryKey)) {
      console.warn(`Missing translation for key: ${primaryKey}`);
      translatedValue = translate.instant(baseKey, messageDetails);
    }
    return translatedValue;
  }

  shareMessage(message) {
    let promise = null;
    if(this.platform.is('cordova')) promise = this.socialProvider.share(message);
    return promise;
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-share-order/rmt-share-order.ts